import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { Drawer, Dropdown, Space } from 'antd';
import { MenuOutlined } from '@ant-design/icons';
import { NavLink } from 'react-router-dom';

import Logo from 'static/images/logo.png';
import { Container } from 'shared/ui/Container';
import { useTheme } from 'hooks/useTheme';
import getEnvConfig from 'utils/config/env';
import { VirtualTourProvider } from 'shared/ui/VirtualTour/VirtualTourProvider';
import { VirtualTour } from 'shared/ui/VirtualTour/VirtualTour';

import { Feedback } from './components/Feedback';
import UserMenu from './components/UserMenu';
import { useProfileData } from '../../../../../hooks/useProfileData';
import { useWindowSize } from '../../../../../hooks/useWindowSize';
import { SubscriptionTag } from './components/Subscription';
import * as S from './Navbar.styles';

interface MenuItem {
    slug: string;
    name: string;
    path: string;
    hide?: boolean;
    children?: MenuItem[];
}

export const Navbar = () => {
    const token = useTheme();
    const { user } = useProfileData();
    const { size } = useWindowSize();
    const [open, setOpen] = useState<boolean>(false);
    const [expandedMenu, setExpandedMenu] = useState<null | string>(null);

    const mainMenu: MenuItem[] = useMemo(
        () => [
            {
                slug: 'communities',
                name: 'Сообщества',
                path: `${getEnvConfig().PUBLIC_PATH}/communities`,
            },
            {
                slug: 'monetization',
                name: 'Монетизация',
                path: `${getEnvConfig().PUBLIC_PATH}/monetization`,
                children: [
                    {
                        slug: 'donate',
                        name: 'Донаты',
                        path: `${
                            getEnvConfig().PUBLIC_PATH
                        }/monetization/donate`,
                    },
                    {
                        slug: 'tariff',
                        name: 'Тарифы',
                        path: `${
                            getEnvConfig().PUBLIC_PATH
                        }/monetization/tariff`,
                    },
                    {
                        slug: 'finances',
                        name: 'Финансы',
                        path: `${
                            getEnvConfig().PUBLIC_PATH
                        }/monetization/finances`,
                    },
                ],
            },
            {
                slug: 'analitycs',
                name: 'Аналитика',
                path: `${getEnvConfig().PUBLIC_PATH}/analitycs`,
            },
            {
                slug: 'shops',
                name: 'Магазины',
                path: `${getEnvConfig().PUBLIC_PATH}/shops`,
            },
            {
                slug: 'admin',
                name: 'Администрирование',
                path: `${getEnvConfig().PUBLIC_PATH}/admin`,
                hide: !user?.admin,
            },
            {
                slug: 'crm',
                name: 'CRM',
                path: `${getEnvConfig().PUBLIC_PATH}/crm`,
            },
        ],
        [user]
    );

    const monetizationChildren =
        mainMenu.find(m => m.slug === 'monetization')?.children || [];

    const items = monetizationChildren.map((submenu, index) => ({
        label: (
            <S.StyledSubmenuLink token={token} to={submenu.path}>
                {submenu.name}
            </S.StyledSubmenuLink>
        ),
        key: String(index + 1),
    }));

    const handleMenuToggle = useCallback((menu: MenuItem) => {
        if (menu.children) {
            setExpandedMenu(menu.slug);
        } else {
            setOpen(prev => !prev);
        }
    }, []);

    const tourRefs = VirtualTourProvider.get();

    const renderMenu = (menu: MenuItem[], mode: 'desktop' | 'mobile') => {
        if (mode === 'desktop') {
            return menu
                .filter(item => !item.hide)
                .map(item => {
                    if (item.children) {
                        return (
                            <Dropdown key={item.slug} menu={{ items }}>
                                <S.StyledLink
                                    token={token}
                                    ref={
                                        item.slug === 'communities'
                                            ? tourRefs?.communitiesRef
                                            : item.slug === 'monetization'
                                            ? tourRefs?.communitiesMonetizationRef
                                            : item.slug === 'analitycs'
                                            ? tourRefs?.communitiesAnalyticsRef
                                            : undefined
                                    }
                                    to={item.children[0].path}
                                >
                                    {item.name}
                                </S.StyledLink>
                            </Dropdown>
                        );
                    }
                    return (
                        <S.StyledLink
                            key={item.slug}
                            token={token}
                            to={item.path}
                        >
                            {item.name}
                        </S.StyledLink>
                    );
                });
        } else {
            return (
                <Space direction={'vertical'} size={'large'}>
                    {menu
                        .filter(elem => !elem.hide)
                        .map(menuItem => (
                            <Fragment key={menuItem.slug}>
                                <S.StyledLink
                                    token={token}
                                    to={menuItem.path}
                                    onClick={() => handleMenuToggle(menuItem)}
                                >
                                    {menuItem.name}
                                </S.StyledLink>
                                {expandedMenu === menuItem.slug &&
                                    menuItem.children && (
                                        <Space
                                            direction={'vertical'}
                                            size={'large'}
                                            style={{ paddingLeft: '1rem' }}
                                        >
                                            {menuItem.children.map(submenu => (
                                                <S.StyledLink
                                                    key={submenu.slug}
                                                    token={token}
                                                    to={submenu.path}
                                                    onClick={() =>
                                                        setOpen(false)
                                                    }
                                                >
                                                    {submenu.name}
                                                </S.StyledLink>
                                            ))}
                                        </Space>
                                    )}
                            </Fragment>
                        ))}
                </Space>
            );
        }
    };

    const RightSection = () => (
        <div>
            <Space size={'middle'}>
                <SubscriptionTag />
                <Feedback />
                <UserMenu />
            </Space>
        </div>
    );

    return (
        <S.StyledWrapper>
            <VirtualTour />
            <Container>
                <S.StyledNav>
                    {size === 'small' ? (
                        <>
                            <S.StyledMobileBurger
                                onClick={() => setOpen(!open)}
                            >
                                <MenuOutlined />
                            </S.StyledMobileBurger>

                            <Drawer
                                placement='left'
                                onClose={() => setOpen(false)}
                                open={open}
                            >
                                {renderMenu(mainMenu, 'mobile')}
                            </Drawer>
                        </>
                    ) : (
                        <>
                            <NavLink to='/'>
                                <img height={22} alt='logo' src={Logo} />
                            </NavLink>
                            <S.StyledTabsContainer>
                                <S.StyledTabs>
                                    {renderMenu(mainMenu, 'desktop')}
                                </S.StyledTabs>
                            </S.StyledTabsContainer>
                        </>
                    )}

                    <RightSection />
                </S.StyledNav>
            </Container>
        </S.StyledWrapper>
    );
};
